import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import Swal from "sweetalert2";
import IsLogin from "../../../Common/IsLogin";
import { ProgressSpinner } from "primereact/progressspinner";
import ReportDateRangeCsv from "../../../Common/ReportDateRangeCSV";

const DailyProductionReport = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const date = new Date();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const [inputs, setInputs] = useState({
    date_from: getCurrentDate(),
    date_to: getCurrentDate(),
  });
  const [loading, setLoading] = useState(false);

  function getCurrentDate() {
    const dateObj = new Date();
    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const fetchData = (dateFromParam, dateToParam) => {
    const dateFrom = dateFromParam || inputs.date_from || getCurrentDate();
    const dateTo = dateToParam || inputs.date_to || getCurrentDate();
    setLoading(true);
    Http.get(
      `/production-and-transfer-list?date_from=${dateFrom}&date_to=${dateTo}`,
    )
      .then((res) => {
        setReceiveData(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const formSubmit = () => {
    fetchData(inputs.date_from, inputs.date_to);
  };

  const calculateRowValues = (item) => {
    const loadedForDate = (item.production_data || []).reduce(
      (sum, p) => sum + Number(p.load_qty || 0),
      0,
    );

    const lossLoad = (item.loss_data || []).reduce(
      (sum, l) => sum + Number(l.before_production_loss_qty || 0),
      0,
    );

    const lossUnload = (item.loss_data || []).reduce(
      (sum, l) => sum + Number(l.after_production_loss_qty || 0),
      0,
    );

    const transferredToSelection = (item.transfer_data || []).reduce(
      (sum, t) =>
        sum +
        (t.is_return === 0 || t.is_return === undefined
          ? Number(t.delivery_qty || 0)
          : 0),
      0,
    );

    const openingStock = 0;
    const totalFirst = openingStock + loadedForDate;
    const totalSecond = lossLoad + lossUnload + transferredToSelection;
    const closingBalance = totalFirst - totalSecond;

    return {
      openingStock,
      loadedForDate,
      totalFirst,
      lossLoad,
      lossUnload,
      transferredToSelection,
      totalSecond,
      closingBalance,
    };
  };

  const totals = receiveData.reduce(
    (acc, item) => {
      const {
        openingStock,
        loadedForDate,
        totalFirst,
        lossLoad,
        lossUnload,
        transferredToSelection,
        totalSecond,
        closingBalance,
      } = calculateRowValues(item);

      return {
        totalOpeningStock: acc.totalOpeningStock + openingStock,
        totalLoadedForDate: acc.totalLoadedForDate + loadedForDate,
        totalFirstTotal: acc.totalFirstTotal + totalFirst,
        totalLossLoad: acc.totalLossLoad + lossLoad,
        totalLossUnload: acc.totalLossUnload + lossUnload,
        totalTransferredToSelection:
          acc.totalTransferredToSelection + transferredToSelection,
        totalSecondTotal: acc.totalSecondTotal + totalSecond,
        totalClosingBalance: acc.totalClosingBalance + closingBalance,
      };
    },
    {
      totalOpeningStock: 0,
      totalLoadedForDate: 0,
      totalFirstTotal: 0,
      totalLossLoad: 0,
      totalLossUnload: 0,
      totalTransferredToSelection: 0,
      totalSecondTotal: 0,
      totalClosingBalance: 0,
    },
  );

  const getCsvData = () => {
    const headers = [
      "Code No.",
      "Name of Products",
      "Shape",
      "Glaze",
      "Opening Stock",
      "Loaded for the Date",
      "Total",
      "Loss Load",
      "Loss Unload",
      "Transferred to Selection Section",
      "Total",
      "Closing Balance",
    ];

    const rows = receiveData.map((item) => {
      const {
        openingStock,
        loadedForDate,
        totalFirst,
        lossLoad,
        lossUnload,
        transferredToSelection,
        totalSecond,
        closingBalance,
      } = calculateRowValues(item);
      return [
        item.product_code,
        item.product_name,
        item.shape,
        item.glaze,
        openingStock,
        loadedForDate,
        totalFirst,
        lossLoad,
        lossUnload,
        transferredToSelection,
        totalSecond,
        closingBalance,
      ];
    });

    const footerRows = [
      [
        "",
        "Total",
        "",
        "",
        totals.totalOpeningStock,
        totals.totalLoadedForDate,
        totals.totalFirstTotal,
        totals.totalLossLoad,
        totals.totalLossUnload,
        totals.totalTransferredToSelection,
        totals.totalSecondTotal,
        totals.totalClosingBalance,
      ],
    ];

    return { headers, rows, footerRows };
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Daily Production Report</h5>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />
            <div className="card pd-20 pd-sm-40">
              <ReportDateRangeCsv
                dateFrom={inputs.date_from}
                dateTo={inputs.date_to}
                onChange={handleChange}
                onSearch={formSubmit}
                csvFilename={`daily-production-report-gk_${inputs.date_from}_to_${inputs.date_to}.csv`}
                getCsvData={getCsvData}
              />
            </div>
          </div>
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              {/* <h6 className="card-body-title mg-sm-b-30">Datatable</h6> */}
              <div className="row">
                <div className="col-12">
                  <div className="text-center">
                    <h5 className="text-uppercase text-sm font-weight-bolder">
                      Artisan Ceramics Limited
                    </h5>
                    <p>
                      House-16, Road-07, Sector-01, Uttara, Dhaka
                      <br />
                      <b>Daily Production Report</b>
                      <br />
                      (GK)
                    </p>
                  </div>
                  <div
                    className="table-responsive p-0 pb-2"
                    style={{ maxHeight: "70vh", overflowY: "auto" }}
                  >
                    <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0 _datatable">
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          backgroundColor: "#fff",
                        }}
                      >
                        <tr>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            Code No.
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            Name of Products
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Shape
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Glaze
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Opening Stock
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Loaded for the Date
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Total
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Loss Load
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Loss Unload
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Transferred to Selection Section
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Total
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Closing Balance
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td
                              className="text-center"
                              colSpan={12}
                              style={{ padding: "2rem" }}
                            >
                              <ProgressSpinner
                                style={{ width: "40px", height: "40px" }}
                                strokeWidth="4"
                              />
                            </td>
                          </tr>
                        ) : receiveData && receiveData.length > 0 ? (
                          receiveData.map((item, index) => {
                            const {
                              openingStock,
                              loadedForDate,
                              totalFirst,
                              lossLoad,
                              lossUnload,
                              transferredToSelection,
                              totalSecond,
                              closingBalance,
                            } = calculateRowValues(item);

                            return (
                              <tr key={index}>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.product_code}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.product_name}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.shape}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.glaze}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {openingStock}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {loadedForDate}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {totalFirst}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {lossLoad}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {lossUnload}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {transferredToSelection}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {totalSecond}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {closingBalance}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                              colSpan={12}
                            >
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            colSpan={4}
                          >
                            Total
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalOpeningStock}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalLoadedForDate}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalFirstTotal}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalLossLoad}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalLossUnload}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTransferredToSelection}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalSecondTotal}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalClosingBalance}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyProductionReport;
